import { Component } from '@theme/component';

/**
 * Remembers which dropdown options the shopper has chosen so morphs
 * can keep "Select A Size" until that specific dropdown is used.
 * Key: `${productId}:${optionName}` → selected value string
 * @type {Map<string, string>}
 */
const dropdownSelections = new Map();

/**
 * Lightweight custom dropdown that syncs to an underlying <select>.
 * Dispatches a native 'change' event on the <select> so variant-picker reacts normally.
 */
class VariantCustomSelect extends Component {
  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    this.#refreshRefs();

    if (!this.#trigger || !this.#list || !this.#select) return;

    // Delegate on the component so listeners survive inner-HTML morphs.
    this.addEventListener('click', this.#onInnerClick, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });

    // After a variant change, variant-picker morphs our children back to the
    // server-rendered placeholder state. Re-apply the shopper's selection.
    document.addEventListener('variant:update', this.#onVariantUpdate, { signal });

    this.#restoreOrApplyPlaceholder();
    this.#close();
  }

  #refreshRefs() {
    this.#trigger = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-custom-select-trigger]'));
    this.#list = /** @type {HTMLElement | null} */ (this.querySelector('[data-custom-select-list]'));
    this.#select = /** @type {HTMLSelectElement | null} */ (this.querySelector('select'));
    this.#label = /** @type {HTMLElement | null} */ (this.querySelector('[data-custom-select-label]'));
  }

  #onVariantUpdate = () => {
    if (!this.isConnected) return;
    // Morph may have swapped inner nodes — re-query before re-applying state.
    this.#refreshRefs();
    this.#restoreOrApplyPlaceholder();
    this.#close();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** @type {HTMLButtonElement | null} */
  #trigger = null;
  /** @type {HTMLElement | null} */
  #list = null;
  /** @type {HTMLSelectElement | null} */
  #select = null;
  /** @type {HTMLElement | null} */
  #label = null;

  /** @returns {string | null} */
  #selectionKey() {
    const productId = this.dataset.productId;
    const optionName = this.dataset.optionName;
    if (!productId || !optionName) return null;
    return `${productId}:${optionName}`;
  }

  /**
   * Keep "Select A Size" until this dropdown is used, even after variant-picker morphs.
   */
  #restoreOrApplyPlaceholder() {
    if (!this.#select) return;

    const key = this.#selectionKey();
    const remembered = key ? dropdownSelections.get(key) : undefined;
    const placeholderLabel = this.dataset.placeholderLabel || 'Select A Size';

    if (remembered) {
      const match = Array.from(this.#select.options).find((option) => option.value === remembered && option.value !== '');
      if (match) {
        this.#setSelectValue(remembered, false);
        if (this.#label) this.#label.textContent = match.textContent?.trim() || remembered;
        this.removeAttribute('data-awaiting-selection');
        return;
      }
    }

    // Default: always show placeholder until the shopper picks a real value.
    this.#applyPlaceholder(placeholderLabel);
  }

  /** @param {string} label */
  #applyPlaceholder(label) {
    if (!this.#select) return;

    for (const option of this.#select.options) {
      option.removeAttribute('selected');
      if (option.value === '') {
        option.selected = true;
        option.setAttribute('selected', 'selected');
      } else {
        option.selected = false;
      }
    }

    // Disabled selected options are ignored by some browsers — force empty value.
    try {
      this.#select.value = '';
    } catch {
      // ignore
    }

    if (this.#label) this.#label.textContent = label;
    this.setAttribute('data-awaiting-selection', '');
  }

  /**
   * @param {string} value
   * @param {boolean} dispatchChange
   */
  #setSelectValue(value, dispatchChange) {
    if (!this.#select) return;

    for (const option of this.#select.options) {
      option.removeAttribute('selected');
      option.selected = option.value === value;
      if (option.selected) option.setAttribute('selected', 'selected');
    }

    this.#select.value = value;

    if (dispatchChange) {
      this.#select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** @param {MouseEvent} e */
  #onInnerClick = (e) => {
    const target = /** @type {HTMLElement | null} */ (e.target instanceof HTMLElement ? e.target : null);
    if (!target) return;

    const trigger = target.closest('[data-custom-select-trigger]');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      this.#refreshRefs();
      const expanded = this.#trigger?.getAttribute('aria-expanded') === 'true';
      if (expanded) this.#close();
      else this.#open();
      return;
    }

    const optionBtn = target.closest('[data-custom-select-option]');
    if (!(optionBtn instanceof HTMLButtonElement)) return;
    if (optionBtn.disabled) return;

    this.#refreshRefs();
    if (!this.#select) return;

    const value = optionBtn.dataset.value ?? '';
    if (!value) return;

    const key = this.#selectionKey();
    if (key) dropdownSelections.set(key, value);

    this.#setSelectValue(value, true);
    if (this.#label) this.#label.textContent = optionBtn.textContent?.trim() || value;
    this.removeAttribute('data-awaiting-selection');

    this.#close();
  };

  /** @param {MouseEvent} e */
  #onDocumentClick = (e) => {
    if (!(e.target instanceof Node)) return;
    if (!this.contains(e.target)) this.#close();
  };

  /** @param {KeyboardEvent} e */
  #onKeyDown = (e) => {
    if (e.key === 'Escape') this.#close();
  };

  #open() {
    if (!this.#trigger || !this.#list) return;
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#list.hidden = false;
  }

  #close() {
    if (!this.#trigger || !this.#list) return;
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#list.hidden = true;
  }
}

if (!customElements.get('variant-custom-select')) {
  customElements.define('variant-custom-select', VariantCustomSelect);
}
